import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {Card, Table, Dropdown, Loader, Dimmer, Icon} from 'semantic-ui-react';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {shortDate} from '../../../common/constants/datesFormat';
import {pageSizes} from '../../../common/constants/lists';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import Header from './header';
import ModalEdit from './modal/editModal';
import PortArrivalAudit from './portArrivalAudit';
import PortArrivalEditor from './portArrivalEditor';
import TablePaginator from '../../../common/components/tablePaginator';
import './inTransitImports.scss';

const {
  fetchProviderImportations,
  editInTransitSelectedInvoice,
  changeProvidersImportationsListPage,
  changeProvidersImportationsListPageSize,
  navigateTo,
} = Actions;

const ImportationsToProvider = () => {
  const permissions = useSelector((state) => state.session.permissions);
  const {updating, loading, pageNumber, pageSize} = useSelector((state) => state.providersImportations);
  const orders = useSelector((state) => state.providersImportations?.listByPage);
  const totalItems = useSelector((state) => state.providersImportations?.list.length);
  const {filters} = useSelector((state) => state.providersImportations);

  const [state, setState] = useState({
    'canAudit': hasAccessPermission(
      permissions,
      resources.ProvidersImportations,
      scopes.Audit
    ),
    'canEdit': hasAccessPermission(
      permissions,
      resources.ProvidersImportations,
      scopes.Edit
    ),
    'isAuditOpen': false,
    'isPortArrivalOpen': false,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (!hasAccessPermission(permissions, resources.ProvidersImportations, scopes.See)) {
      navigateTo(paths.AccessDenied);
    }
    dispatch(changeProvidersImportationsListPage(0));
  }, []);
  
  useEffect(() => {
    dispatch(fetchProviderImportations({'status': filters.status}));
  }, [filters.status]);

  const handleAuditIconClick = (invoice) => {
    setState({'isAuditOpen': true});
    dispatch(editInTransitSelectedInvoice(invoice));
  };

  const handleAuditArrivalEditClick = (invoice) => {
    setState({'isPortArrivalOpen': true});
    dispatch(editInTransitSelectedInvoice(invoice));
  };

  const handleAuditClose = () => {
    setState({'isAuditOpen': false});
    dispatch(editInTransitSelectedInvoice(null));
  };

  const handlePortArrivalClose = () => {
    setState({'isPortArrivalOpen': false});
    dispatch(editInTransitSelectedInvoice(null));
  };

  const onPageClick = (value) => {
    dispatch(changeProvidersImportationsListPage(value));
  };

  const onPageSizeClick = (event, data) => {
    dispatch(changeProvidersImportationsListPageSize(data.value));
  };

  const renderArrivalEditIcon = (element) => {
    const {canEdit} = state;

    if (!canEdit) {
      return null;
    }
    return (
      <Icon
        link
        size='large'
        name='write'
        onClick={() => handleAuditArrivalEditClick(element)}
      />
    );
  };

  const renderETAEditIcon = (index, element) => {
    const {canEdit} = state;

    if (!canEdit) {
      return null;
    }
    return (
      <ModalEdit
        invoice={element.code}
        defaultDate={element.etd}
        position={pageNumber * pageSize + index}
      />
    );
  };

  const renderActionsColumn = (element) => {
    const {canAudit} = state;

    if (!canAudit) {
      return false;
    }
    return (
      <Icon
        link
        name='history'
        size='large'
        onClick={() => handleAuditIconClick(element)}
      />
    );
  };

  const renderOrders = () => {
    if (!orders.length) {
      return (
        <Table.Row>
          <Table.Cell textAlign='center' colSpan='100%'>
            No hay órdenes en tránsito
          </Table.Cell>
        </Table.Row>
      );
    }

    return orders.map((element, index) => {
      const formattedArrivalDate = element.arrivalDate ?
        moment(element.arrivalDate).format(shortDate) :
        '';
      const formattedEtdUpdated = element.etdDate ?
        moment(element.etdDate).format(shortDate) :
        '';

      return (
        <Table.Row key={index}>
          <Table.Cell>{element.providerName}</Table.Cell>
          <Table.Cell textAlign='center'>{element.orderType}</Table.Cell>
          <Table.Cell textAlign='center'>{element.transport}</Table.Cell>
          <Table.Cell textAlign='center'>{element.code}</Table.Cell>
          <Table.Cell textAlign='right'>
            {element.portArrivalDate ?
              moment(element.portArrivalDate).format(shortDate) :
              ''}
            {renderArrivalEditIcon(element)}
            {renderActionsColumn(element)}
          </Table.Cell>
          <Table.Cell textAlign='center'>
            {formattedArrivalDate.toUpperCase()}
          </Table.Cell>
          <Table.Cell textAlign='right'>
            {formattedEtdUpdated.toUpperCase()}
            {renderETAEditIcon(index, element)}
          </Table.Cell>
          <Table.Cell textAlign='center'>{element.observation}</Table.Cell>
        </Table.Row>
      );
    });
  };

  const renderLoaderOrOrders = () => {
    if (loading) {
      return (
        <Table.Row>
          <Table.Cell colSpan='100%'>
            <Loader active inline='centered' />
          </Table.Cell>
        </Table.Row>
      );
    }
    return renderOrders();
  };

  const renderPagination = () => {
    if (loading || totalItems <= pageSize) {
      return null;
    }
    return (
      <Table.Footer>
        <Table.Row textAlign='center'>
          <Table.HeaderCell colSpan='100%'>
            <div className='table-pagination'>
              <span className='page-sizer'>
                <strong>Items por página</strong>
                <Dropdown
                  selection
                  upward
                  compact
                  value={pageSize}
                  options={pageSizes}
                  onChange={onPageSizeClick}
                />
              </span>
              <TablePaginator
                itemsCount={totalItems}
                itemsPerPage={pageSize}
                selectedPage={pageNumber}
                onOptionClick={onPageClick}
              />
            </div>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Footer>
    );
  };

  const renderImportationsTable = () => {
    return (
      <Table striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell textAlign='center'>Proveedor</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>Tipo</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>Vía</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>
              Factura Proveedor
            </Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>ETA Puerto</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>ETA Estandar</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>
              ETA Actualizada
            </Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>
              Observaciones TDE
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{renderLoaderOrOrders()}</Table.Body>
        {renderPagination()}
      </Table>
    );
  };

  return (
    <div className='provider-importations'>
      <Header />

      {updating && (
        <Dimmer active inverted>
          <Loader inverted inline='centered' content={'actualizando'} />
        </Dimmer>
      )}

      <Card fluid raised className='primary-content'>
        {renderImportationsTable()}
      </Card>

      <PortArrivalEditor
        isOpen={state.isPortArrivalOpen}
        closeHandler={handlePortArrivalClose}
      />
      <PortArrivalAudit
        isOpen={state.isAuditOpen}
        closeHandler={handleAuditClose}
      />
    </div>
  );
};

export default ImportationsToProvider;
